import { AlertTriangle, AlertCircle, CheckCircle2, HelpCircle, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { TopIssue, Alert, ConditionStatus } from "@/types/vision360";

interface Vision360SummaryProps {
  keySummary: string[];
  topIssues: TopIssue[];
  alerts: Alert[];
}

const getStatusIcon = (status: ConditionStatus) => {
  switch (status) {
    case "ativo": return <AlertCircle className="h-3.5 w-3.5 text-amber-500" />;
    case "controlado": return <CheckCircle2 className="h-3.5 w-3.5 text-blue-500" />;
    case "resolvido": return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
    case "investigacao": return <HelpCircle className="h-3.5 w-3.5 text-gray-500" />;
    default: return <HelpCircle className="h-3.5 w-3.5 text-gray-400" />;
  }
};

const getSeverityBadge = (severity: number) => {
  const config = {
    1: { label: "Leve", variant: "outline" as const, className: "border-yellow-400 text-yellow-600" },
    2: { label: "Leve-Mod", variant: "outline" as const, className: "border-yellow-500 text-yellow-600" },
    3: { label: "Moderado", variant: "outline" as const, className: "border-amber-500 text-amber-600" },
    4: { label: "Grave", variant: "destructive" as const, className: "" },
    5: { label: "Crítico", variant: "destructive" as const, className: "animate-pulse" },
  };
  
  const c = config[severity as keyof typeof config] || config[1];
  return (
    <Badge variant={c.variant} className={cn("text-[10px] px-1.5 py-0", c.className)}>
      {c.label}
    </Badge>
  );
};

const getAlertIcon = (type: string) => {
  switch (type) {
    case "risco_alto": return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case "atencao": return <AlertCircle className="h-4 w-4 text-amber-500" />;
    default: return <TrendingUp className="h-4 w-4 text-blue-500" />;
  }
};

const getAlertBg = (type: string) => {
  switch (type) {
    case "risco_alto": return "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900";
    case "atencao": return "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900";
    default: return "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900";
  }
};

export function Vision360Summary({ keySummary, topIssues, alerts }: Vision360SummaryProps) {
  return (
    <div className="space-y-4">
      {/* Alertas */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Alertas
          </h4>
          <div className="space-y-2">
            {alerts.map((alert, i) => (
              <Card key={i} className={cn("p-3 border", getAlertBg(alert.type))}>
                <div className="flex items-start gap-2">
                  <div className="shrink-0 mt-0.5">{getAlertIcon(alert.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm break-words">{alert.title}</p>
                    <p className="text-xs text-muted-foreground break-words">{alert.message}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Resumo Rápido */}
      {keySummary.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground">Resumo Rápido</h4>
          <ul className="space-y-1.5">
            {keySummary.map((bullet, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="text-primary mt-1">•</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Top 5 Problemas */}
      {topIssues.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground">Principais Problemas</h4>
          <div className="space-y-2">
            {topIssues.slice(0, 5).map((issue) => (
              <div
                key={issue.rank}
                className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
              >
                <span className="text-xs font-bold text-muted-foreground w-5">
                  #{issue.rank}
                </span>
                {getStatusIcon(issue.status)}
                <span className="flex-1 text-sm font-medium truncate">{issue.title}</span>
                {getSeverityBadge(issue.severity)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
